import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import './styles/page.css';
import './styles/tables.css';
import './styles/cards.css';
import { BrowserRouter, Routes, Link, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { fetchSavedInsights } from './shared/api/savedInsights';
import { MENU_GROUPS, getFirstPermittedPath } from './config/menuConfig';
import { getServerConfigurationErrorMessage } from './shared/config/serverConfig';
import useAccountBranding from './shared/hooks/useAccountBranding';
import { accountLogoImageSrc, isAccountLogoExplicitlyHidden } from './shared/utils/accountLogoUrl';
import { getUserDisplayName } from './shared/utils/userDisplay';
import HorizontalScrollHint from './components/HorizontalScrollHint';

import Logout from "./pages/Logout";
import UserAdmin from "./pages/UserAdmin";
import Project from "./pages/Project";
import OperatingPlan from "./pages/OperatingPlan";
import OpsPlanDashboard from "./pages/OpsPlanDashboard";
import StockManagement from "./pages/StockManagement";
import VolunteerOpportunities, { VolunteerSignupBrowse } from "./pages/VolunteerOpportunities";
import VolunteerParticipants from "./pages/VolunteerParticipants";
import VolunteerImpactDashboard from "./pages/VolunteerImpactDashboard";
import Task from "./pages/Task";
import FinancialInsights from "./pages/FinancialInsights";
import FinancialReports from "./pages/FinancialReports";
import FinancialAccounts from "./pages/FinancialAccounts";
import Transaction from "./pages/Transaction";
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FirebaseAuthActionRedirect from './pages/FirebaseAuthActionRedirect';
import Register from './pages/Register';
import AccountAdmin from './pages/AccountAdmin';
import Solutions from './pages/Solutions';
import SolutionDetail from './pages/SolutionDetail';
import ProgramManagement from './pages/ProgramManagement';
import EventManagement from './pages/EventManagement';
import {
  buildEventsManagementLegacyRedirectPath,
  buildEventsManagementPath,
  EVENT_MGMT_ROUTE_PERMISSIONS,
  EVENT_MGMT_TAB_CLOSE_RECORD,
  EVENT_MGMT_TAB_EVENTS,
  EVENT_MGMT_TAB_PARTICIPANTS,
} from './shared/utils/eventManagementTabs';
import EventForm from './pages/EventForm';
import EventDashboard from './pages/EventDashboard';
import TimeBlocking from './pages/TimeBlocking';
import EventCalendar from './pages/EventCalendar';
import EventVanityLinkRedirect from './pages/EventVanityLinkRedirect';
import CancelRegistration from './pages/CancelRegistration';
import VerifyEmail from './pages/VerifyEmail';
import EventQRCheckIn from './pages/EventQRCheckIn';
import AIProjectBuilder from './pages/AIProjectBuilder';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';
import Communication from './pages/Communication';
import StripeConnection from './pages/StripeConnection';
import RolePermissionAdmin from './pages/RolePermissionAdmin';
import DataManagement from './pages/DataManagement';
import UserCustomizationAdmin from './pages/UserCustomizationAdmin';
import AssetManagement from './pages/AssetManagement';
import ShieldCareDoctors from './pages/ShieldCareDoctors';
import ShieldCarePharmacies from './pages/ShieldCarePharmacies';
import ShieldCareMedications from './pages/ShieldCareMedications';
import ShieldCareIntakeLogs from './pages/ShieldCareIntakeLogs';
import Surveys from './pages/Surveys';
import SurveyResponse from './pages/SurveyResponse';
import SurveyResults from './pages/SurveyResults';
import SurveyDashboard from './pages/SurveyDashboard';
import SurveyInvitationList from './pages/SurveyInvitationList';
import SurveyQuestionsInfo from './pages/SurveyQuestionsInfo';
import SurveyDetails from './pages/SurveyDetails';
import ProfileInfo from './pages/ProfileInfo';
import JobApplyExtensionConnect from './pages/JobApplyExtensionConnect';
import ProfileInfoModal from './components/ProfileInfoModal';
import SmsOptInPromptModal from './components/SmsOptInPromptModal';

const Children = lazy(() => import('./pages/Children'));

class AppErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Something went wrong.';
      const isServerDown = /failed to fetch|network|unavailable/i.test(msg);
      return (
        <div style={{ padding: '24px', maxWidth: '560px', margin: '40px auto', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#842029', marginBottom: '12px' }}>Something went wrong</h2>
          <p style={{ color: '#333', marginBottom: '16px' }}>
            {isServerDown
              ? 'Server is unavailable or not responding. Please check that the server is running and try again.'
              : msg}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ padding: '8px 16px', cursor: 'pointer' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children, loginPath = '/Login' }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div>Loading…</div>;
  if (!user) {
    const returnPath = `${location.pathname}${location.search}`;
    return <Navigate to={`${loginPath}?returnUrl=${encodeURIComponent(returnPath)}`} replace />;
  }
  return children;
}

function RequireSuperAdmin({ children, fallbackPath }) {
  const { userInfo, loading, hasPermission, hasRole } = useAuth();
  if (loading) return <div>Loading…</div>;
  if (hasRole('Super Admin')) return children;
  const to = fallbackPath ?? (userInfo
    ? getFirstPermittedPath(hasPermission, { isSuperAdmin: false })
    : '/Login');
  return <Navigate to={to} replace />;
}

function RequireParentRole({ children, fallbackPath }) {
  const { userInfo, loading, hasPermission, hasRole } = useAuth();
  if (loading) return <div>Loading…</div>;
  if (hasRole('Parent')) return children;
  const to = fallbackPath ?? (userInfo
    ? getFirstPermittedPath(hasPermission, { isSuperAdmin: hasRole('Super Admin') })
    : '/Login');
  return <Navigate to={to} replace />;
}

/** Data-driven: allow access if Super Admin or user has the given permission. Redirects to first permitted menu path when lacking permission. */
function RequirePermission({ permissionId, children, fallbackPath }) {
  const { userInfo, loading, hasPermission, hasRole } = useAuth();
  if (loading) return <div>Loading…</div>;
  if (!permissionId || hasPermission(permissionId)) return children;
  // When userInfo is missing (e.g. auth error), avoid sending to a page that expects it
  const to = fallbackPath ?? (userInfo
    ? getFirstPermittedPath(hasPermission, { isSuperAdmin: hasRole('Super Admin') })
    : '/Login');
  return <Navigate to={to} replace />;
}

/** Allow access if user has any of the given permissions. */
function RequireAnyPermission({ permissionIds, children, fallbackPath }) {
  const { userInfo, loading, hasPermission, hasRole } = useAuth();
  if (loading) return <div>Loading…</div>;
  const allowed = Array.isArray(permissionIds) && permissionIds.some((id) => hasPermission(id));
  if (allowed) return children;
  const to = fallbackPath ?? (userInfo
    ? getFirstPermittedPath(hasPermission, { isSuperAdmin: hasRole('Super Admin') })
    : '/Login');
  return <Navigate to={to} replace />;
}

function Nav() {
  const { user, userInfo, hasPermission, hasRole } = useAuth();
  const { accountBranding, brandingLoading, subdomain: tenantSubdomain } = useAccountBranding();
  const [openMenu, setOpenMenu] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [menuSavedInsights, setMenuSavedInsights] = useState([]);
  const [navDropdownPos, setNavDropdownPos] = useState(null);
  const navRef = useRef(null);
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;
  const profileHeaderName = getUserDisplayName(userInfo);

  useEffect(() => {
    function handleClickOutside(e) {
      const target = e.target;
      if (navRef.current && navRef.current.contains(target)) return;
      if (target && typeof target.closest === 'function' && target.closest('.nav-dropdown-portal')) return;
      setOpenMenu(null);
      setNavDropdownPos(null);
      setOpenProfile(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Load saved insights on login (and when account_id / user_id is set) — user + account scoped
  useEffect(() => {
    if (!account_id) {
      setMenuSavedInsights([]);
      return;
    }
    let cancelled = false;
    fetchSavedInsights(account_id, user_id)
      .then((list) => {
        if (!cancelled) setMenuSavedInsights(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setMenuSavedInsights([]);
      });
    return () => { cancelled = true; };
  }, [account_id, user_id]);

  const handleMenuNavClick = () => {
    setOpenMenu(null);
    setNavDropdownPos(null);
  };

  // Refetch saved insights when Projects menu is opened so new/deleted insights appear (Financial Insights)
  useEffect(() => {
    if (openMenu !== 'project' || !account_id) return;
    let cancelled = false;
    fetchSavedInsights(account_id, user_id)
      .then((list) => {
        if (!cancelled) setMenuSavedInsights(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [openMenu, account_id, user_id]);

  // No global top nav for signed-out users (login and other public routes use in-page chrome).
  if (!user) {
    return null;
  }

  const toggleMenu = (menu, event) => {
    if (openMenu === menu) {
      setOpenMenu(null);
      setNavDropdownPos(null);
      return;
    }
    const btn = event?.currentTarget;
    if (btn && typeof btn.getBoundingClientRect === 'function') {
      const rect = btn.getBoundingClientRect();
      const approxWidth = 220;
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - approxWidth - 12));
      setNavDropdownPos({ top: Math.round(rect.bottom + 6), left: Math.round(left) });
    } else {
      setNavDropdownPos(null);
    }
    setOpenMenu(menu);
  };
  const parentStyle = { color: 'white', textDecoration: 'none', fontSize: '14px', cursor: 'pointer' };

  // Data-driven: show group if any child has permission (or for project, also show saved Financial Insights)
  // Roles & Permissions is Super Admin only: route uses RequireSuperAdmin, so hide menu for others to avoid "nothing happens" on click
  const visibleGroups = MENU_GROUPS.map((grp) => {
    const visibleChildren = (grp.children || []).filter((c) => {
      if (c.superAdminOnly && !hasRole('Super Admin')) return false;
      if (!c.permissionId || !hasPermission(c.permissionId)) return false;
      return true;
    });
    const showGroup = grp.key === 'project' ? (visibleChildren.length > 0 || menuSavedInsights.length > 0) : visibleChildren.length > 0;
    return { ...grp, visibleChildren, showGroup };
  }).filter((grp) => grp.showGroup);

  const signedInImg = accountLogoImageSrc(userInfo?.logo_url);
  const signedInHidden = isAccountLogoExplicitlyHidden(userInfo?.logo_url);
  const publicImg = accountLogoImageSrc(accountBranding?.logo_url);
  const publicHidden = isAccountLogoExplicitlyHidden(accountBranding?.logo_url);

  let navBrandInner;
  if (userInfo) {
    if (signedInHidden) navBrandInner = null;
    else if (signedInImg) {
      navBrandInner = (
        <span className="nav-brand-logo-wrap">
          <img src={signedInImg} alt="" className="nav-brand-logo" />
        </span>
      );
    } else {
      const name = (userInfo.account_name || '').trim();
      navBrandInner = name || 'UCS LLC';
    }
  } else if (tenantSubdomain && brandingLoading) {
    // Avoid flashing default "UCS LLC" before /account/by-site resolves (e.g. logo NONE → empty nav).
    navBrandInner = null;
  } else if (publicHidden) {
    navBrandInner = null;
  } else if (publicImg) {
    navBrandInner = (
      <span className="nav-brand-logo-wrap">
        <img
          src={publicImg}
          alt=""
          className="nav-brand-logo"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </span>
    );
  } else if (tenantSubdomain && (accountBranding?.full_name || '').trim()) {
    navBrandInner = (accountBranding.full_name || '').trim();
  } else if (!tenantSubdomain) {
    navBrandInner = 'UCS LLC';
  } else {
    navBrandInner = null;
  }

  const openGroup = visibleGroups.find((grp) => grp.key === openMenu) || null;
  const closeNavMenu = () => {
    setOpenMenu(null);
    setNavDropdownPos(null);
  };

  return (
    <>
    <nav ref={navRef}>
      <h1 className="nav-brand">
        {navBrandInner}
      </h1>
      {user && (
        <div className="nav-right">
          <HorizontalScrollHint
            className="h-scroll-hint--nav"
            as="ul"
            scrollerClassName="nav-menu"
            aria-label="Main menu"
            onScroll={closeNavMenu}
          >
            {visibleGroups.map((grp) => (
              <li key={grp.key} className="nav-has-dropdown">
                <div className={`nav-dropdown-wrapper${openMenu === grp.key ? ' nav-dropdown-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-parent"
                    style={parentStyle}
                    aria-expanded={openMenu === grp.key}
                    aria-haspopup="menu"
                    onClick={(e) => toggleMenu(grp.key, e)}
                  >
                    {grp.label}
                  </button>
                </div>
              </li>
            ))}
          </HorizontalScrollHint>
          <div className="nav-profile-wrap">
            <button
              type="button"
              className="nav-profile-btn"
              onClick={(e) => { e.stopPropagation(); setOpenProfile((p) => !p); }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={profileHeaderName ? `Profile, ${profileHeaderName}` : 'Profile'}
              aria-expanded={openProfile}
            >
              {profileHeaderName ? (
                <span className="nav-profile-header-name">{profileHeaderName}</span>
              ) : null}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="10" r="3" />
                <path d="M6.168 18.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855" />
              </svg>
            </button>
            {openProfile && (
              <div className="nav-profile-card">
                <div className="nav-profile-name">{profileHeaderName || '—'}</div>
                {userInfo?.account_name && <div className="nav-profile-account">{userInfo.account_name}</div>}
                {userInfo?.roles && userInfo.roles.length > 0 && (
                  <div className="nav-profile-role">{userInfo.roles.join(', ')}</div>
                )}
                <button type="button" className="nav-profile-logout" onClick={() => { setOpenProfile(false); setProfileModalOpen(true); }}>Profile info</button>
                {hasRole('Parent') ? (
                  <Link to="/Children" className="nav-profile-logout" onClick={() => setOpenProfile(false)}>Children</Link>
                ) : null}
                <Link to="/Logout" className="nav-profile-logout" onClick={() => setOpenProfile(false)}>Logout</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
    {openGroup && navDropdownPos
      ? createPortal(
          <ul
            className="nav-dropdown-portal"
            role="menu"
            aria-label={openGroup.label}
            style={{ top: navDropdownPos.top, left: navDropdownPos.left }}
          >
            {openGroup.visibleChildren.map((item) => (
              <li key={item.path} role="none">
                <Link
                  role="menuitem"
                  to={item.path}
                  onClick={handleMenuNavClick}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            {openGroup.key === 'project' && menuSavedInsights.map((insight) => (
              <li key={insight.id} role="none">
                <Link
                  role="menuitem"
                  to={`/FinancialInsights?insightId=${encodeURIComponent(insight.id)}`}
                  onClick={closeNavMenu}
                >
                  {insight.name}
                </Link>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null}
    <ProfileInfoModal
      open={profileModalOpen}
      onClose={() => setProfileModalOpen(false)}
      editable
      firstName={userInfo?.first_name}
      lastName={userInfo?.last_name}
      email={userInfo?.email_id || user?.email}
      phone={userInfo?.phone_number}
      birthday={userInfo?.birthday}
      gender={userInfo?.gender}
      parentName={userInfo?.parent_name}
      parentEmail={userInfo?.parent_email}
      parentPhone={userInfo?.parent_phone}
      grade={userInfo?.grade}
      school={userInfo?.school}
      createdDate={userInfo?.created_date}
    />
    {userInfo ? <SmsOptInPromptModal /> : null}
  </>
  );
}

function EventDetailRedirect() {
  const { eventId } = useParams();
  return <Navigate to={buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS, detail: eventId })} replace />;
}

function EventsManagementLegacyRedirect({ tab, pathParamKey }) {
  const location = useLocation();
  const params = useParams();
  const pathParams = pathParamKey && params[pathParamKey]
    ? { [pathParamKey]: params[pathParamKey] }
    : {};
  return (
    <Navigate
      to={buildEventsManagementLegacyRedirectPath(tab, {
        search: location.search,
        pathParams,
      })}
      replace
    />
  );
}

function App() {
  const serverConfigError = getServerConfigurationErrorMessage();
  if (serverConfigError) {
    return (
      <div
        style={{
          padding: '28px',
          maxWidth: '640px',
          margin: '48px auto',
          fontFamily: 'system-ui, sans-serif',
          lineHeight: 1.5,
        }}
      >
        <h1 style={{ color: '#842029', marginBottom: '16px', fontSize: '1.35rem' }}>Configuration required</h1>
        <p style={{ color: '#333', marginBottom: '12px' }}>{serverConfigError}</p>
        <p style={{ color: '#555', fontSize: '0.95rem' }}>
          After updating environment variables, rebuild or restart the dev server so the new values are picked up.
        </p>
      </div>
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <div>
          <div>
            <Nav />
          </div>
          <AppErrorBoundary>
            <main className="app-content">
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/Login" element={<Login />} />
              <Route path="/terms" element={<Navigate to="/terms.html" replace />} />
              <Route path="/Terms" element={<Navigate to="/terms.html" replace />} />
              <Route path="/privacy" element={<Navigate to="/privacy.html" replace />} />
              <Route path="/Privacy" element={<Navigate to="/privacy.html" replace />} />
              <Route path="/ForgotPassword" element={<ForgotPassword />} />
              <Route path="/ResetPassword" element={<ResetPassword />} />
              <Route path="/__/auth/action" element={<FirebaseAuthActionRedirect />} />
              <Route path="/Register" element={<RequireAuth><RequireSuperAdmin><Register /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/Dashboard" element={<RequireAuth><RequireAnyPermission permissionIds={['menu.project_dashboard', 'menu.task_dashboard']}><Dashboard /></RequireAnyPermission></RequireAuth>} />
              <Route path="/Task" element={<RequireAuth><Task /></RequireAuth>} />
              <Route path="/FinancialInsights" element={<RequireAuth><FinancialInsights /></RequireAuth>} />
              <Route path="/FinancialReports" element={<RequireAuth><RequirePermission permissionId="menu.financial_reports"><FinancialReports /></RequirePermission></RequireAuth>} />
              <Route path="/FinancialAccounts" element={<RequireAuth><RequirePermission permissionId="menu.financial_accounts"><FinancialAccounts /></RequirePermission></RequireAuth>} />
              <Route path="/Transaction" element={<RequireAuth><RequirePermission permissionId="menu.transaction"><Transaction /></RequirePermission></RequireAuth>} />
              <Route path="/AIProjectBuilder" element={<RequireAuth><AIProjectBuilder /></RequireAuth>} />
              <Route path="/TimeBlocking" element={<RequireAuth><RequirePermission permissionId="menu.time_blocking"><TimeBlocking /></RequirePermission></RequireAuth>} />
              <Route path="/Project" element={<RequireAuth><Project /></RequireAuth>} />
              <Route path="/AssetManagement" element={<RequireAuth><RequireAnyPermission permissionIds={['menu.asset_management', 'menu.insurance_management', 'menu.vendor', 'menu.rental_leases', 'menu.rent_ledger']}><AssetManagement /></RequireAnyPermission></RequireAuth>} />
              <Route path="/InsuranceManagement" element={<RequireAuth><Navigate to="/AssetManagement?tab=insurance" replace /></RequireAuth>} />
              <Route path="/OperatingPlan" element={<RequireAuth><RequirePermission permissionId="menu.operating_plan"><OperatingPlan /></RequirePermission></RequireAuth>} />
              <Route path="/OpsPlanDashboard" element={<RequireAuth><RequirePermission permissionId="menu.ops_plan_dashboard"><OpsPlanDashboard /></RequirePermission></RequireAuth>} />
              <Route path="/Vendor" element={<RequireAuth><Navigate to="/AssetManagement?tab=vendors" replace /></RequireAuth>} />
              <Route path="/Vendor/TopRated" element={<RequireAuth><Navigate to="/AssetManagement?tab=vendors" replace /></RequireAuth>} />
              <Route path="/StockManagement" element={<RequireAuth><RequirePermission permissionId="menu.stock_management"><StockManagement /></RequirePermission></RequireAuth>} />
              {/* Former Rental Homes routes — redirect to Asset Management tabs */}
              <Route path="/RentalProperty" element={<RequireAuth><Navigate to="/AssetManagement" replace /></RequireAuth>} />
              <Route path="/RentalLease" element={<RequireAuth><Navigate to="/AssetManagement?tab=leases" replace /></RequireAuth>} />
              <Route path="/RentLedger" element={<RequireAuth><Navigate to="/AssetManagement?tab=ledger" replace /></RequireAuth>} />
              <Route path="/VolunteerOpportunities" element={<RequireAuth><RequirePermission permissionId="menu.volunteer_opportunities"><VolunteerOpportunities /></RequirePermission></RequireAuth>} />
              <Route path="/VolunteerParticipants" element={<RequireAuth><RequirePermission permissionId="menu.volunteer_opportunities"><VolunteerParticipants /></RequirePermission></RequireAuth>} />
              <Route path="/VolunteerSignup" element={<RequireAuth><RequirePermission permissionId="menu.volunteer_signup"><VolunteerSignupBrowse /></RequirePermission></RequireAuth>} />
              <Route path="/VolunteerImpactDashboard" element={<RequireAuth><RequirePermission permissionId="menu.volunteer_impact_dashboard"><VolunteerImpactDashboard /></RequirePermission></RequireAuth>} />
              <Route path="/UserAdmin" element={<RequireAuth><RequirePermission permissionId="menu.user_admin"><UserAdmin /></RequirePermission></RequireAuth>} />
              <Route path="/Solutions" element={<RequireAuth><RequireSuperAdmin><Solutions /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/Solution/:id" element={<RequireAuth><RequireSuperAdmin><SolutionDetail /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/ShieldCare/Doctors" element={<RequireAuth><RequirePermission permissionId="menu.shield_care_doctors"><ShieldCareDoctors /></RequirePermission></RequireAuth>} />
              <Route path="/ShieldCare/Pharmacies" element={<RequireAuth><RequirePermission permissionId="menu.shield_care_pharmacies"><ShieldCarePharmacies /></RequirePermission></RequireAuth>} />
              <Route path="/ShieldCare/Medications" element={<RequireAuth><RequirePermission permissionId="menu.shield_care_medications"><ShieldCareMedications /></RequirePermission></RequireAuth>} />
              <Route path="/ShieldCare/IntakeLogs" element={<RequireAuth><RequirePermission permissionId="menu.shield_care_intake_logs"><ShieldCareIntakeLogs /></RequirePermission></RequireAuth>} />
              <Route path="/ProgramManagement" element={<RequireAuth><RequirePermission permissionId="menu.programs"><ProgramManagement /></RequirePermission></RequireAuth>} />
              <Route path="/Programs" element={<Navigate to="/ProgramManagement?tab=program" replace />} />
              <Route path="/ProgramOrganizers" element={<Navigate to="/ProgramManagement?tab=programLead" replace />} />
              <Route path="/ProgramOrganizers/:programId" element={<Navigate to="/ProgramManagement?tab=programLead" replace />} />
              <Route path="/EventCalendar" element={<EventCalendar />} />
              <Route path="/Children" element={<RequireAuth><RequireParentRole><Suspense fallback={<div>Loading…</div>}><Children /></Suspense></RequireParentRole></RequireAuth>} />
              <Route path="/e/:eventSlug" element={<EventVanityLinkRedirect />} />
              <Route path="/cancel-registration" element={<CancelRegistration />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/MyEvents" element={<Navigate to="/EventCalendar" replace />} />
              <Route path="/Events" element={<RequireAuth><RequireAnyPermission permissionIds={EVENT_MGMT_ROUTE_PERMISSIONS}><EventManagement /></RequireAnyPermission></RequireAuth>} />
              <Route path="/EventDashboard" element={<RequireAuth><RequirePermission permissionId="menu.event_dashboard"><EventDashboard /></RequirePermission></RequireAuth>} />
              <Route path="/RegistrationMetrics" element={<Navigate to="/EventDashboard?tab=registrationMetrics" replace />} />
              <Route path="/ClosedEventsMetrics" element={<Navigate to="/EventDashboard?tab=closedEventsMetrics" replace />} />
              <Route path="/Events/new" element={<RequireAuth><RequirePermission permissionId="menu.events"><EventForm /></RequirePermission></RequireAuth>} />
              <Route path="/Events/:eventId/edit" element={<RequireAuth><RequirePermission permissionId="menu.events"><EventForm /></RequirePermission></RequireAuth>} />
              <Route path="/Events/:eventId" element={<RequireAuth><RequireAnyPermission permissionIds={['menu.events', 'menu.event_participants']}><EventDetailRedirect /></RequireAnyPermission></RequireAuth>} />
              <Route path="/CloseEventRecord" element={<RequireAuth><RequireAnyPermission permissionIds={EVENT_MGMT_ROUTE_PERMISSIONS}><EventsManagementLegacyRedirect tab={EVENT_MGMT_TAB_CLOSE_RECORD} /></RequireAnyPermission></RequireAuth>} />
              <Route path="/EventQRCheckIn" element={<RequireAuth><RequirePermission permissionId="menu.event_qr_checkin"><EventQRCheckIn /></RequirePermission></RequireAuth>} />
              <Route path="/EventParticipants" element={<RequireAuth><RequireAnyPermission permissionIds={EVENT_MGMT_ROUTE_PERMISSIONS}><EventsManagementLegacyRedirect tab={EVENT_MGMT_TAB_PARTICIPANTS} /></RequireAnyPermission></RequireAuth>} />
              <Route path="/EventParticipants/:eventId" element={<RequireAuth><RequireAnyPermission permissionIds={EVENT_MGMT_ROUTE_PERMISSIONS}><EventsManagementLegacyRedirect tab={EVENT_MGMT_TAB_PARTICIPANTS} pathParamKey="eventId" /></RequireAnyPermission></RequireAuth>} />
              <Route path="/Surveys" element={<RequireAuth><RequirePermission permissionId="menu.surveys"><Surveys /></RequirePermission></RequireAuth>} />
              <Route path="/SurveyDashboard" element={<RequireAuth><RequirePermission permissionId="menu.survey_dashboard"><SurveyDashboard /></RequirePermission></RequireAuth>} />
              <Route path="/SurveyResults" element={<Navigate to="/SurveyDashboard?tab=surveyResults" replace />} />
              <Route path="/SurveyInvitation" element={<RequireAuth><RequirePermission permissionId="menu.survey_dashboard"><SurveyInvitationList /></RequirePermission></RequireAuth>} />
              <Route path="/SurveyQuestionsInfo" element={<RequireAuth><RequirePermission permissionId="menu.surveys"><SurveyQuestionsInfo /></RequirePermission></RequireAuth>} />
              <Route path="/Surveys/:surveyId/details" element={<RequireAuth><RequireAnyPermission permissionIds={['menu.surveys', 'menu.survey_dashboard']}><SurveyDetails /></RequireAnyPermission></RequireAuth>} />
              <Route path="/Surveys/:surveyId/results" element={<RequireAuth><RequireAnyPermission permissionIds={['menu.surveys', 'menu.survey_dashboard']}><SurveyResults /></RequireAnyPermission></RequireAuth>} />
              <Route path="/SurveyResponse" element={<SurveyResponse />} />
              <Route path="/AccountAdmin" element={<RequireAuth><RequirePermission permissionId="menu.account_admin"><AccountAdmin /></RequirePermission></RequireAuth>} />
              <Route path="/RolePermissionAdmin" element={<RequireAuth><RequireSuperAdmin><RolePermissionAdmin /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/DataManagement" element={<RequireAuth><RequireSuperAdmin><DataManagement /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/UserCustomizations" element={<RequireAuth><RequireSuperAdmin><UserCustomizationAdmin /></RequireSuperAdmin></RequireAuth>} />
              <Route path="/Pricing" element={<RequireAuth><RequirePermission permissionId="menu.pricing"><Pricing /></RequirePermission></RequireAuth>} />
              <Route path="/Communication" element={<RequireAuth><RequirePermission permissionId="menu.communication"><Communication /></RequirePermission></RequireAuth>} />
              <Route path="/StripeConnection" element={<RequireAuth><RequirePermission permissionId="menu.stripe_connection"><StripeConnection /></RequirePermission></RequireAuth>} />
              <Route path="/ProfileInfo" element={<RequireAuth><ProfileInfo /></RequireAuth>} />
              <Route path="/JobApplyExtensionConnect" element={<JobApplyExtensionConnect />} />
              <Route path="/Logout" element={<RequireAuth><Logout /></RequireAuth>} />
            </Routes>
            </main>
          </AppErrorBoundary>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
